import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Snackbar,
    Avatar,
    Grow
} from '@mui/material';
import {
    AssignmentTurnedIn,
    Person,
    Devices,
    Refresh,
    Business,
    Router
} from '@mui/icons-material';
import axios from 'axios';

function DeviceAllocationPage() {
    const [users, setUsers] = useState([]);
    const [devices, setDevices] = useState([]);
    const [subOffices, setSubOffices] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedDevice, setSelectedDevice] = useState('');
    const [selectedSubOffice, setSelectedSubOffice] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [usersRes, devicesRes, officesRes] = await Promise.all([
                axios.get('/api/v1/auth/users', { headers }),
                axios.get('/api/v1/devices/', { headers }),
                axios.get('/api/v1/sub-office/', { headers })
            ]);

            setUsers(usersRes.data);
            setDevices(devicesRes.data);
            setSubOffices(officesRes.data);
        } catch (err) {
            console.error('Failed to fetch data:', err);
            setError('Failed to sync system entities. Audit your connection.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAllocate = async () => {
        if (!selectedUser || !selectedDevice) {
            setError('Please specify both Operator and Terminal Identity.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/v1/devices/allocate', {
                device_id: selectedDevice,
                user_id: selectedUser || null,
                sub_office_id: selectedSubOffice || null
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setSuccess(`Terminal ${selectedDevice} successfully provisioned.`);
            setSelectedDevice('');
            fetchData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Allocation failed. Terminal or Operator may be invalid.');
        } finally {
            setLoading(false);
        }
    };

    const getUserName = (id) => {
        const user = users.find(u => u.id === id);
        return user ? user.username : 'Unallocated';
    };

    const getOfficeName = (id) => {
        const office = subOffices.find(o => o.id === id);
        return office ? office.name : 'Central HQ';
    };

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: -1 }}>
                        Node Allocation Center
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Provision hardware entities to operators and hubs
                    </Typography>
                </Box>
                <IconButton onClick={fetchData} sx={{ bgcolor: 'rgba(0, 180, 219, 0.05)', color: 'primary.main' }}>
                    <Refresh />
                </IconButton>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} lg={4}>
                    <Paper className="glass-card" sx={{ p: 4, height: '100%' }}>
                        <Typography variant="h6" sx={{ color: 'text.primary', mb: 3, fontWeight: 900 }}>
                            Strategic Provisioning
                        </Typography>

                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Operational Hub</InputLabel>
                            <Select
                                value={selectedSubOffice}
                                onChange={(e) => setSelectedSubOffice(e.target.value)}
                                label="Operational Hub"
                                sx={{ borderRadius: 3 }}
                            >
                                <MenuItem value=""><em>None (Central HQ)</em></MenuItem>
                                {subOffices.map((office) => (
                                    <MenuItem key={office.id} value={office.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Business sx={{ fontSize: 18, color: 'primary.main' }} />
                                            {office.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Target Operator</InputLabel>
                            <Select
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                                label="Target Operator"
                                sx={{ borderRadius: 3 }}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {users.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Person sx={{ fontSize: 18, color: 'primary.main' }} />
                                            {user.username}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 4 }}>
                            <InputLabel>Available Node Identifier</InputLabel>
                            <Select
                                value={selectedDevice}
                                onChange={(e) => setSelectedDevice(e.target.value)}
                                label="Available Node Identifier"
                                sx={{ borderRadius: 3 }}
                            >
                                {devices.filter(d => !d.user_id && !d.sub_office_id).map((device) => (
                                    <MenuItem key={device.device_id} value={device.device_id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Router sx={{ fontSize: 18, color: 'primary.main' }} />
                                            {device.device_id}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleAllocate}
                            disabled={loading}
                            sx={{
                                py: 1.8,
                                fontWeight: 900,
                                borderRadius: 3,
                                background: 'linear-gradient(45deg, #00B4DB, #0083B0)',
                                boxShadow: '0 8px 16px rgba(0, 180, 219, 0.2)'
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm Allocation'}
                        </Button>
                    </Paper>
                </Grid>

                <Grid item xs={12} lg={8}>
                    <Grow in>
                        <TableContainer component={Paper} className="glass-card" sx={{ borderRadius: 4, overflow: 'hidden' }}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead sx={{ bgcolor: 'rgba(0, 180, 219, 0.05)' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>Node Component</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Hub Assignment</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Primary Operator</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Connectivity</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {devices.map((device) => (
                                        <TableRow key={device.device_id} sx={{ '&:hover': { bgcolor: 'rgba(0, 180, 219, 0.02)' }, transition: 'background 0.2s' }}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                                                        <Router sx={{ fontSize: 18 }} />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{device.device_id}</Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{device.device_type}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                                    <Business sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                        {getOfficeName(device.sub_office_id)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                                    <Person sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                        {getUserName(device.user_id)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={device.status.toUpperCase()}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: device.status === 'online' ? 'rgba(71, 207, 115, 0.1)' : 'rgba(0,0,0,0.05)',
                                                        color: device.status === 'online' ? 'success.main' : 'text.secondary',
                                                        fontWeight: 900,
                                                        borderRadius: 1
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grow>
                </Grid>
            </Grid>

            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert severity="error" sx={{ borderRadius: 3, fontWeight: 700 }}>{error}</Alert>
            </Snackbar>
            <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess('')}>
                <Alert severity="success" sx={{ borderRadius: 3, fontWeight: 700 }}>{success}</Alert>
            </Snackbar>
        </Box>
    );
}

export default DeviceAllocationPage;
